import { FC, useEffect } from 'react';
import { Button } from '@cfa/react-core';
import styled from 'styled-components';
import { IconWithShadow } from '../Styled/IconWithShadow';
import { themeSizes, themeColors } from '../../styles/globalStyleVars';
import {
  IconTicket,
  IconSearch,
  IconCalendarOff,
  IconAlertOctagon,
  IconTool,
  IconBuildingStore,
  IconExternalLink,
} from '@tabler/icons-react';
import { emptyStateOptions } from '../../utils/config/emptyStateConfig';
import { useNavigate } from 'react-router-dom';
import { callEvent } from '../../utils/Amplitude/AmplitudeUtilities';
import { useLocationStore } from '../../stores/locationStore';
import { TypographyStyled } from '../Styled/Typography';

const AlertRow = styled.div<{ $page: string; $filterState: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  &:first-of-type {
    margin-bottom: ${themeSizes.sm};
    margin-top: ${({ $filterState, $page }) =>
      $page === 'home'
        ? themeSizes.md
        : ['noFormsFound', 'facilitiesEquipmentSearch'].includes($filterState)
          ? '0px'
          : '88px'};
  }
  &:last-of-type {
    ${({ $filterState }) =>
      $filterState !== 'noFormsFound' && 'margin-bottom: 328px'};
  }
  white-space: pre-wrap;
`;

const AlertSubtitle = styled.p`
  font-size: ${themeSizes.md};
  line-height: ${themeSizes.lg};
  color: ${themeColors.colorGray_5};
  margin-bottom: 0px;
  margin-top: ${themeSizes.sm};
  white-space: pre-line;
`;

interface Props {
  filterState: string;
  page: string;
  analytics?: {
    name: string;
    [key: string]: number | string | boolean | null | undefined;
  };
  hideCTAButton?: boolean;
  matchesFacilityTerm?: boolean;
  searchText?: string;
}

const EmptyStateAlerts: FC<Props> = ({
  analytics,
  filterState,
  hideCTAButton,
  matchesFacilityTerm,
  page,

  searchText,
}) => {
  const { activeLocation } = useLocationStore();
  const stateConfig = emptyStateOptions.find(
    emptyStateOption => emptyStateOption.filterState === filterState
  );
  if (!stateConfig) return <></>;
  const navigate = useNavigate();
  const getHelpWithEquipmentFormId = '83112ab8c3ab4610f77a30ff0501312e';

  useEffect(() => {
    if (!analytics) return;
    const { name, ...rest } = analytics;
    callEvent(name, rest);
  }, []);

  const generateIconComponent = () => {
    const tablerIconProps = {
      size: 38,
      color: themeColors.colorGray_6,
    };

    switch (stateConfig.icon) {
      case 'search':
        return {
          tablerIcon: <IconSearch {...tablerIconProps} />,
        };
      case 'calendar':
        return {
          tablerIcon: <IconCalendarOff {...tablerIconProps} />,
        };
      case 'alert_octagon':
        return {
          tablerIcon: <IconAlertOctagon {...tablerIconProps} />,
        };
      case 'wrench':
        return {
          tablerIcon: <IconTool {...tablerIconProps} />,
        };
      case 'building_store':
        return {
          tablerIcon: <IconBuildingStore {...tablerIconProps} />,
        };
      default:
        return {
          tablerIcon: <IconTicket {...tablerIconProps} />,
        };
    }
  };

  const generateButtonConfig = (filterState?: string) => {
    switch (filterState) {
      case 'facilitiesEquipmentSearch':
        return {
          route: '/forms/524969f7c3d10a1076145523e4013188',
          text: 'Open a facilities request',
        };
      case 'global':
        return {
          route: '/',
          text: 'Go to Homepage',
        };
      case 'equipmentNotFound':
      case 'equipmentDetails':
        return {
          route: '/equipment',
          text: 'Equipment Page',
        };
      case 'noResultsFoundEquipment':
        return {
          route: `/forms/${getHelpWithEquipmentFormId}`,
          text: 'Open a ticket',
        };
      default:
        return {
          route: '/forms',
          text: 'Open a Ticket',
        };
    }
  };

  const buttonConfig =
    stateConfig.showSubmitTicketButton && !hideCTAButton
      ? generateButtonConfig(filterState)
      : null;

  const generateTitleConfig = (filterState: string, title?: string) => {
    switch (filterState) {
      case 'facilitiesEquipmentSearch': {
        if (!title) return title;
        const boldWord = stateConfig?.boldWord;
        if (boldWord) {
          const parts = title.split(boldWord);
          return (
            <>
              {parts[0]}
              <span style={{ fontWeight: 'bold' }}>{boldWord}</span>
              {parts[1]}
            </>
          );
        }
        return title;
      }
      default:
        return title;
    }
  };

  const handleClick = (buttonConfig: { route: string }) => {
    const formRoute = `/forms/${getHelpWithEquipmentFormId}`;
    navigate(buttonConfig.route);

    if (buttonConfig.route === formRoute) {
      callEvent('form entry point', {
        'entry point': 'equipment search',
        'form id': getHelpWithEquipmentFormId,
        'form name': 'ham_data_correction',
        locationNum: activeLocation,
      });
    }

    if (matchesFacilityTerm) {
      callEvent('open ticket for facilities equipment', {
        locationNum: activeLocation,
        'search term': searchText,
      });
    }
  };

  return (
    <div id='empty-state-wrapper' style={{ marginTop: themeSizes.lg }}>
      <AlertRow className='row' $page={page} $filterState={filterState}>
        <IconWithShadow {...generateIconComponent()} />
        <TypographyStyled
          variant={filterState === 'facilitiesEquipmentSearch' ? 'body1' : 'h3'}
          style={{ marginTop: themeSizes.md }}
        >
          {filterState === 'noResultsFoundEquipment' && searchText
            ? `No results for "${searchText}" at this Restaurant`
            : generateTitleConfig(filterState, stateConfig.emptyStateTitle)}
        </TypographyStyled>
      </AlertRow>
      {stateConfig.emptyStateMessage &&
        !hideCTAButton &&
        !matchesFacilityTerm && (
          <AlertRow className='row' $page={page} $filterState={filterState}>
            <AlertSubtitle>{stateConfig.emptyStateMessage}</AlertSubtitle>
          </AlertRow>
        )}
      {buttonConfig && (
        <AlertRow className='row' $page={page} $filterState={filterState}>
          {filterState === 'noResultsFoundEquipment' && (
            <AlertSubtitle style={{ marginTop: themeSizes.lg }}>
              Need help, but can&apos;t find your equipment?
            </AlertSubtitle>
          )}
          <Button
            onPress={() => handleClick(buttonConfig)}
            size={'md'}
            variant='outlined'
            style={{
              width: 'auto',
              marginTop:
                filterState === 'noResultsFoundEquipment'
                  ? '6px'
                  : themeSizes.md,
            }}
            data-testid='empty-state-button'
          >
            {buttonConfig.text}
            {filterState === 'facilitiesEquipmentSearch' && (
              <IconExternalLink
                size={20}
                style={{ marginLeft: themeSizes.sm }}
              />
            )}
          </Button>
        </AlertRow>
      )}
    </div>
  );
};

export default EmptyStateAlerts;
